import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchStrategicIssues, updateStrategicIssue,
  fetchValueChains,
  fetchFinancialGoals,
  fetchStrategicGoals,
  fetchCustomerValues,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmStrategicIssueEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedBPIds, setSelectedBPIds] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [businessProcesses, setBusinessProcesses] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [fullData, setFullData] = useState(null);

  const selectedBPs = selectedBPIds.map((id) => businessProcesses.find((bp) => bp.businessProcessId === id)).filter(Boolean);

  // 指標は選択されたバリューチェーン（業務プロセス）の indicators を集約
  const sourceIndicators = useMemo(() => {
    const all = selectedBPs.flatMap((bp) => bp.indicators || []);
    const seen = new Set();
    return all.filter((ind) => {
      if (seen.has(ind.indicatorName)) return false;
      seen.add(ind.indicatorName);
      return true;
    });
  }, [selectedBPs]);

  useEffect(() => {
    (async () => {
      try {
        const [issues, bps, fgoals, sgoals, cvals] = await Promise.all([
          fetchStrategicIssues(businessDomainId),
          fetchValueChains(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
        ]);
        setBusinessProcesses(Array.isArray(bps) ? bps : []);
        setFinancialGoals(Array.isArray(fgoals) ? fgoals : []);
        setStrategicGoals(Array.isArray(sgoals) ? sgoals : []);
        setCustomerValues(Array.isArray(cvals) ? cvals : []);
        const found = (Array.isArray(issues) ? issues : []).find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
          setSelectedNames((found.indicators || []).map((ind) => ind.indicatorName));
          // バックエンドのバグ（addAll のみで clear なし）により DB に重複が蓄積している場合があるため重複排除
          const uniqueBPIds = [...new Set((found.businessProcesses || []).map((bp) => bp.businessProcessId))];
          setSelectedBPIds(uniqueBPIds);
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const sources = sourceIds.map((id) => allSources.find((s) => s.successFactorId === id)).filter(Boolean);
      await updateStrategicIssue({
        ...fullData,
        successFactorName,
        successFactorDescription,
        businessProcesses: selectedBPs,
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/strategic-issues/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  const allSources = [...financialGoals, ...customerValues, ...strategicGoals];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("strategicIssue.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("valueChain.list.title")}</InputLabel>
              <Select
                multiple
                value={selectedBPIds}
                onChange={(e) => setSelectedBPIds(e.target.value)}
                input={<OutlinedInput label={t("valueChain.list.title")} />}
              >
                {businessProcesses.map((bp) => (
                  <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                    {bp.businessProcessName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("strategicIssue.sources.label")} />}
              >
                {financialGoals.length > 0 && [
                  <MenuItem key="__fg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("financialGoal.list.title")} ──</MenuItem>,
                  ...financialGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {customerValues.length > 0 && [
                  <MenuItem key="__cv_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("customerValue.list.title")} ──</MenuItem>,
                  ...customerValues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {strategicGoals.length > 0 && [
                  <MenuItem key="__sg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("strategicGoal.list.title")} ──</MenuItem>,
                  ...strategicGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                    {ind.indicatorName}（{ind.unit}）
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/strategic-issues/${successFactorId}`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
